// lib
import React, { useCallback, useRef } from 'react'
import { RouteComponentProps } from 'react-router'

// components
import MetaTags from 'src/components/meta_tags/meta_tags'
import GroupFeed from 'src/containers/smart_components/feeds/group_feed'
import PageTitleGroupFeed from 'src/containers/smart_components/page_title/page_title_group_feed'
import LayoutNoMap from 'src/layout/layout_no_map'

// constants & interfaces
import { FEED_DEFAULT_PER_PAGE, GROUP_OVERVIEW, GROUP_PARTICIPATIONS_PAGE_SIZE } from 'src/constants/feed'
import { GROUP_FEED_TITLE } from 'src/constants/meta_tags'

// helpers
import { usePageTracking, useIsPostCreateOpen } from 'src/pages/hooks/hooks'
import { useSelectUser } from 'src/reducers/user/user_hooks'
import { useGetGroupIdFromUrlParams } from '../hooks_shared/use_url_params'

// graphql
import { useGroup } from '@lopo-tech/lopo-api'
import { useHistory } from 'react-router-dom'
import { FRONTEND_GROUP_PATH } from '../constants/urls'
import HeaderGroup from '../components/header/subheaders/pages/group/header_group'
import LoadingSpinner from '../components/loading_spinner/loading_spinner'

const GroupFeedPage: React.FC<RouteComponentProps> = ({ match }) => {
  const history = useHistory()
  const user = useSelectUser()
  const slug = useGetGroupIdFromUrlParams()

  const { data, loading, error, fetchMore } = useGroup({
    variables: {
      slug,
      page: 1,
      perPage: FEED_DEFAULT_PER_PAGE,
      groupParticipationsPage: 1,
      groupParticipationsPerPage: GROUP_PARTICIPATIONS_PAGE_SIZE,
      // TODO: check up on image sizing here
      width: 500,
      height: 500
    }
  })

  // we need to ensure that the callback always has the latest loading state
  // to avoid multiple fetchMore calls being triggered due to the getNextPage
  // being in a throttled fashion
  const loadingRef = useRef(loading)
  loadingRef.current = loading
  const getNextPage = useCallback(() => {
    if (data?.group?.topics?.[0]?.contentItems?.pageInfo.nextPage && !loadingRef.current) {
      fetchMore({ variables: { page: data.group.topics[0].contentItems.pageInfo.nextPage } })
    }
  }, [fetchMore, data?.group?.topics?.[0]?.contentItems?.pageInfo.nextPage])

  if (error) {
    console.error('Error fetching group:', error)
  }

  const group = data?.group ?? null

  if (!loading && !group) {
    history.push(FRONTEND_GROUP_PATH)
  }

  const isPostCreateOpen = useIsPostCreateOpen(match)
  const title = (group && GROUP_FEED_TITLE(group?.name)) ?? undefined
  const description = (group && group.description) ?? undefined

  usePageTracking({ pageTitle: title, user })

  return (
    <>
      <MetaTags description={description} title={title} />
      <LayoutNoMap
        activeNavigationItem={GROUP_OVERVIEW}
        subHeader={<HeaderGroup group={group} showMoreButton={true} />}
        withHeaderMargin={true}
        pageTitle={<PageTitleGroupFeed title={title} />}
      >
        {loading || !group
          ? (
            <LoadingSpinner shown={true} />
          )
          : (
            <GroupFeed
              loading={loading}
              group={group}
              getNextPage={getNextPage}
              postCreateOpen={isPostCreateOpen}
            />
          )}
      </LayoutNoMap>
    </>
  )
}

export default GroupFeedPage
